import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, CircularProgress, Stack, Tooltip } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { useCallback, useContext, useEffect, useState } from 'react';

import SelectProgram from '@/components/signup_components/SelectProgram';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import usePartner from '@/hooks/usePartner';
import { PartnerProps } from '@/models/Partner';
import { PartnerDashboardDetailsProps, PartnerDashboardProps } from '@/models/PartnerDashboard';
import { GrowthAwardProgramIds, ProgramNames } from '@/models/Program';
import {
  CustomExportButton,
  dataGridStringCompare,
  GridLocaleTextOverride,
} from '@/utils/dataGridUtil';

import PartnerDataGridDetails from './PartnerDataGridDetails';
import { LocaleContext } from '@/Contexts/LocaleContext';

function ManagePartners() {
  const { setAlert } = useContext(SnackbarContext);
  const { getPartnersByProgram, getAdvancedPartnerExport } = usePartner();
  const { selectedProgramId, selectedProgramInfo, updateSelectedProgramId } =
    useContext(SelectedProgramContext);
  const { locale } = useContext(LocaleContext);

  const { allCounties, allCountries, allStates } = useContext(ConfigContext);

  const { partnersAll, updatePartnersAll } = useContext(SelectedPartnerContext);

  const { userType } = useContext(UsersContext);

  const [partnerCols, setPartnerCols] = useState<GridColDef<PartnerDashboardProps>[]>([]);
  const [loading, setLoading] = useState<boolean>(
    selectedProgramId != null && selectedProgramId !== ProgramNames._
  );
  const [selectedPartners, setSelectedPartners] = useState<PartnerDashboardProps[]>([]);

  const [partnersInfoMessage, setPartnersInfoMessage] = useState<string>(
    'Please select a program to load partners.'
  );

  const getRowPartnerYearsRecognized = useCallback((partner: PartnerDashboardProps) => {
    return partner.yearsAwarded ?? 0;
  }, []);

  const handleRowUpdate = useCallback(
    (updatedRow: PartnerProps) => {
      const updatedPartners = selectedPartners.map((partner: PartnerDashboardDetailsProps) => {
        if (partner.id === updatedRow.id) {
          const updatedPartner = {
            ...partner,
            ...updatedRow,
            primaryContactEmail:
              updatedRow?.primaryContact?.email != null
                ? updatedRow.primaryContact.email
                : partner.primaryContactEmail,
          };
          return updatedPartner;
        }
        return partner;
      });

      partnersAll[selectedProgramId] = updatedPartners;

      updatePartnersAll(partnersAll);
    },
    [partnersAll, selectedPartners, selectedProgramId, updatePartnersAll]
  );

  const handlePartnerExport = useCallback(() => {
    if (selectedProgramId != null) {
      getAdvancedPartnerExport(selectedProgramId)
        .then(() => {
          setAlert({
            severity: 'success',
            message: 'Export successful.',
          });
        })
        .catch((error: Error) => {
          setAlert({
            severity: 'error',
            message: `Export failed. ${error.message}`,
          });
        });
    }
  }, [getAdvancedPartnerExport, selectedProgramId, setAlert]);

  useEffect(() => {
    const cols: GridColDef[] = [
      {
        field: 'id',
        headerName: 'Partner ID',
        flex: 0.5,
      },
      {
        field: 'partnerName',
        headerName: 'Partner Name',
        flex: 1,
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'partnerGroupName',
        headerName: 'Partner Group',
        flex: 1,
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
        renderCell: (params: GridRenderCellParams) => {
          const partner: PartnerDashboardProps = params.row as PartnerDashboardProps;

          return partner?.partnerGroupName ?? '';
        },
      },
      {
        field: 'yearsAwarded',
        headerName: 'Years Awarded',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          const partner: PartnerDashboardProps = params.row as PartnerDashboardProps;
          return partner?.yearsAwarded ?? 0;
        },
      },
      ...(selectedProgramInfo?.id != null && GrowthAwardProgramIds.includes(selectedProgramInfo.id)
        ? [
            {
              field: 'growthAwardYearsAwarded',
              headerName: 'Growth Award Years',
              flex: 1,
              renderCell: (params: GridRenderCellParams) => {
                const partner: PartnerDashboardProps = params.row as PartnerDashboardProps;
                return partner?.growthAwardYearsAwarded ?? 0;
              },
            },
          ]
        : []),
      {
        field: 'primaryContactEmail',
        headerName: 'Primary Contact Email',
        flex: 1,
      },
      {
        field: 'applicantEmail',
        headerName: 'Applicant Email',
        flex: 1,
      },
      {
        field: 'fullAddress',
        headerName: 'Location',
        flex: 2.5,
        renderHeader: () => {
          return (
            <Tooltip title='Location is based on the partner location and can be updated in the Edit Partner Details modal.'>
              <Box className='grid-header-with-info'>
                Location <InfoOutlinedIcon fontSize={'small'} />
              </Box>
            </Tooltip>
          );
        },
        valueGetter: (_, row: PartnerDashboardProps) => {
          return `${row.city != null ? row.city + ', ' : ''} ${
            row.countyName != null && row.countyName != 'International' ? row.countyName + ',' : ''
          } ${
            row.stateName != null && row.stateName != 'International' ? row.stateName + ',' : ''
          } ${row.countryName != null ? row.countryName : ''}`;
        },
      },
    ];

    setPartnerCols(cols);
  }, [
    allCounties,
    allCountries,
    allStates,
    getRowPartnerYearsRecognized,
    selectedProgramId,
    selectedProgramInfo,
    userType,
  ]);

  useEffect(() => {
    if (selectedProgramId == null) {
      setPartnersInfoMessage('Please select a program to load partners.');
    } else {
      if (loading) {
        setPartnersInfoMessage('Loading...');
      } else if (!loading && selectedPartners.length == 0) {
        setPartnersInfoMessage('No partners found for the selected program.');
      }
    }
  }, [loading, selectedProgramId, selectedPartners]);

  useEffect(() => {
    if (
      partnersAll != null &&
      selectedProgramId != null &&
      selectedProgramId !== ProgramNames._ &&
      partnersAll[selectedProgramId] != null
    ) {
      const p = partnersAll[selectedProgramId];
      setSelectedPartners(p);
      setLoading(false);
    }
  }, [partnersAll, selectedProgramId]);

  const fetchData = useCallback(
    async (programId: ProgramNames, abortController: AbortController) => {
      if (
        programId != null &&
        programId !== ProgramNames._ &&
        (partnersAll == null || !Object.keys(partnersAll).includes(programId.toString()))
      ) {
        setSelectedPartners([]);
        setLoading(true);
        let partners: PartnerDashboardProps[] = await getPartnersByProgram(
          programId,
          abortController
        );
        partners = partners.filter((p) => p != null);
        setSelectedPartners(partners);

        const sp = {
          ...partnersAll,
          [programId]: partners,
        };
        updatePartnersAll(sp);
      } else if (
        programId != null &&
        programId !== ProgramNames._ &&
        partnersAll != null &&
        Object.keys(partnersAll).includes(programId.toString())
      ) {
        setSelectedPartners(partnersAll[programId]);
      }
    },
    [getPartnersByProgram, partnersAll, updatePartnersAll]
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchData(selectedProgramId, controller);
    return () => controller.abort('Program changed. Data fetching aborted.');
  }, [fetchData, selectedProgramId]);

  const GridToolBarCustom = useCallback(
    (props: GridToolbarContainerProps) => {
      return (
        <GridToolbarContainer {...props}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
          <CustomExportButton
            onClick={handlePartnerExport}
            disabled={selectedProgramId == null || selectedProgramId === ProgramNames._}
          />
          <div style={{ flex: 1 }}></div>
          <GridToolbarQuickFilter debounceMs={1500} />
        </GridToolbarContainer>
      );
    },
    [handlePartnerExport, selectedProgramId]
  );

  const datagridSx = {
    marginLeft: '10px',
    marginRight: '10px',
    flex: '1 1 auto',
    '.grid-header-with-info': {
      display: 'flex',
      alignItems: 'space-between',
      justifyItems: 'center',
      gap: 1,
    },
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '70vh',
      }}
    >
      <Box
        width='calc(100% - 10px)'
        display='flex'
        flex='1 0 auto'
        marginLeft='10px'
        marginBottom='1em'
      >
        <SelectProgram onChange={(event) => updateSelectedProgramId(Number(event.target.value))} />
      </Box>
      <DataGridPremium
        showToolbar
        localeText={GridLocaleTextOverride(locale)}
        getDetailPanelContent={(params) => {
          if (params.row.id == null) return <></>;

          return params != null ? (
            <PartnerDataGridDetails partnerRow={params} updateRow={handleRowUpdate} />
          ) : (
            <> </>
          );
        }}
        getDetailPanelHeight={() => 'auto'}
        disableVirtualization
        disableDensitySelector
        getRowId={(row: PartnerDashboardProps) => row.id!}
        sx={datagridSx}
        rows={selectedPartners ?? []}
        columns={partnerCols}
        initialState={{ density: 'compact' }}
        autoPageSize
        pagination
        slots={{
          noRowsOverlay: () => {
            return (
              <Stack height='100%' alignItems='center' justifyContent='center'>
                <div>
                  <div
                    style={{
                      width: 'auto',
                      display: 'inline-block',
                    }}
                  >
                    {partnersInfoMessage}
                  </div>

                  {loading && (
                    <div
                      style={{
                        display: 'inline-block',
                        position: 'relative',
                        top: '3px',
                        width: '20px',
                        height: '20px',
                        marginLeft: '10px',
                      }}
                    >
                      <CircularProgress size='16px' />
                    </div>
                  )}
                </div>
              </Stack>
            );
          },
          noResultsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              <div>No Partners</div>
            </Stack>
          ),
          toolbar: GridToolBarCustom,
        }}
      />
    </Box>
  );
}

export default ManagePartners;
